import React, { useState, useEffect, useCallback } from "react";
import FeatureCard from "./FeatureCard";

export interface FeatureCardType {
  id: string | number;
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface FeatureCarouselProps {
  featureCards: FeatureCardType[];
}

const useBreakpoint = () => {
  const [config, setConfig] = useState({
    translateX: "50%",
    isMobile: false,
    containerHeight: "380px",
  });

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width < 576) {
        // Mobile
        setConfig({
          translateX: "110%", // Cards fully off-screen on mobile
          isMobile: true,
          containerHeight: "320px",
        });
      } else if (width < 768) {
        // Small tablets
        setConfig({
          translateX: "55%",
          isMobile: false,
          containerHeight: "350px",
        });
      } else if (width < 992) {
        // Tablets
        setConfig({
          translateX: "60%",
          isMobile: false,
          containerHeight: "380px",
        });
      } else {
        // Desktop
        setConfig({
          translateX: "60%",
          isMobile: false,
          containerHeight: "400px",
        });
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return config;
};

const FeatureCarousel: React.FC<FeatureCarouselProps> = ({ featureCards }) => {
  const [currentFeatureIndex, setCurrentFeatureIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  const totalFeatureCards = featureCards.length;
  const { translateX, isMobile, containerHeight } = useBreakpoint();

  const nextFeatureSlide = useCallback(() => {
    setCurrentFeatureIndex((prevIndex) => (prevIndex + 1) % totalFeatureCards);
  }, [totalFeatureCards]);

  const prevFeatureSlide = useCallback(() => {
    setCurrentFeatureIndex(
      (prevIndex) => (prevIndex - 1 + totalFeatureCards) % totalFeatureCards
    );
  }, [totalFeatureCards]);

  useEffect(() => {
    const timer = setTimeout(() => {
      nextFeatureSlide();
    }, 5000);
    return () => clearTimeout(timer);
  }, [currentFeatureIndex, nextFeatureSlide]);

  // Touch handlers for mobile swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    const touch = e.targetTouches[0];
    if (touch) {
      setTouchStart(touch.clientX);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const touch = e.targetTouches[0];
    if (touch) {
      setTouchEnd(touch.clientX);
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (distance > minSwipeDistance) {
      nextFeatureSlide();
    } else if (distance < -minSwipeDistance) {
      prevFeatureSlide();
    }
  };

  const getCardStyle = (
    cardRelation: number,
    index: number
  ): React.CSSProperties => {
    const baseStyle: React.CSSProperties = {
      transition: "all 0.5s cubic-bezier(0.4, 0, 0.2, 1)",
      position: "absolute",
      width: "100%",
      height: "100%",
      top: 0,
      left: 0,
    };

    const isHovered = hoveredIndex === index;
    const sideCardScale = isMobile ? 0.85 : 0.75;

    switch (cardRelation) {
      case 0:
        return {
          ...baseStyle,
          opacity: 1,
          transform: "scale(1)",
          zIndex: 20,
          pointerEvents: isMobile ? "auto" : "none",
        };
      case 1:
        return {
          ...baseStyle,
          opacity: isHovered ? 0.8 : isMobile ? 0 : 0.6,
          transform: `scale(${sideCardScale}) translateX(${translateX})`,
          zIndex: 10,
          cursor: "pointer",
          pointerEvents: isMobile ? "none" : "auto",
        };
      case -1:
        return {
          ...baseStyle,
          opacity: isHovered ? 0.8 : isMobile ? 0 : 0.6,
          transform: `scale(${sideCardScale}) translateX(-${translateX})`,
          zIndex: 10,
          cursor: "pointer",
          pointerEvents: isMobile ? "none" : "auto",
        };
      case 2:
        return {
          ...baseStyle,
          opacity: 0,
          transform: `scale(${sideCardScale}) translateX(${translateX})`,
          zIndex: 0,
          pointerEvents: "none",
        };
      case -2:
        return {
          ...baseStyle,
          opacity: 0,
          transform: `scale(${sideCardScale}) translateX(-${translateX})`,
          zIndex: 0,
          pointerEvents: "none",
        };
      default:
        return {
          ...baseStyle,
          opacity: 0,
          transform: "scale(0.5)",
          zIndex: 0,
          pointerEvents: "none",
        };
    }
  };

  const handleCardClick = (clickRelativePos: number) => {
    if (clickRelativePos === 1) {
      nextFeatureSlide();
    } else if (clickRelativePos === -1) {
      prevFeatureSlide();
    }
  };

  return (
    <section className="mb-3 mb-md-5">
      <div className="container-fluid px-2 px-sm-3 px-md-5">
        <div
          className="position-relative mx-auto"
          style={{
            width: "100%",
            maxWidth: isMobile ? "100vw" : "90vw",
            height: containerHeight,
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="position-relative d-flex justify-content-center align-items-center"
            style={{
              width: "100%",
              height: "100%",
            }}
          >
            {featureCards.map((card, index) => {
              let cardRelation: number;
              const centerIdx = currentFeatureIndex;
              const nextVisIdx = (currentFeatureIndex + 1) % totalFeatureCards;
              const prevVisIdx =
                (currentFeatureIndex - 1 + totalFeatureCards) %
                totalFeatureCards;

              if (index === centerIdx) {
                cardRelation = 0;
              } else if (index === nextVisIdx) {
                cardRelation = 1;
              } else if (index === prevVisIdx) {
                cardRelation = -1;
              } else {
                let diff = index - centerIdx;
                const half = totalFeatureCards / 2;
                if (diff > half) diff -= totalFeatureCards;
                else if (diff < -half) diff += totalFeatureCards;
                cardRelation = diff > 0 ? 2 : -2;
              }

              let clickRelativePos: number;
              if (index === currentFeatureIndex) clickRelativePos = 0;
              else if (index === (currentFeatureIndex + 1) % totalFeatureCards)
                clickRelativePos = 1;
              else if (
                index ===
                (currentFeatureIndex - 1 + totalFeatureCards) %
                  totalFeatureCards
              )
                clickRelativePos = -1;
              else clickRelativePos = 2;

              return (
                <div
                  key={card.id}
                  style={getCardStyle(cardRelation, index)}
                  onMouseEnter={() => {
                    if (
                      !isMobile &&
                      (clickRelativePos === 1 || clickRelativePos === -1)
                    ) {
                      setHoveredIndex(index);
                    }
                  }}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCardClick(clickRelativePos);
                  }}
                >
                  <FeatureCard card={card} isMobile={isMobile} />
                </div>
              );
            })}
          </div>

          {/* Mobile Navigation Dots */}
          {isMobile && (
            <div
              className="position-absolute w-100 d-flex justify-content-center gap-2"
              style={{ bottom: "-30px" }}
            >
              {featureCards.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentFeatureIndex(index)}
                  className="border-0 rounded-circle"
                  style={{
                    width: "8px",
                    height: "8px",
                    backgroundColor:
                      index === currentFeatureIndex
                        ? "#fff"
                        : "rgba(255,255,255,0.4)",
                    padding: 0,
                    transition: "all 0.3s ease",
                  }}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeatureCarousel;
